import { useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command } from "cmdk";
import type { CaseFieldOption } from "../../../model/caseReview";
import { CaseReviewIcon } from "../CaseReviewIcon";
import { EMPTY_VALUE_LABEL } from "../../../constants/caseReviewLabels";

interface Props {
  id: string; value: number | string | null; options: CaseFieldOption[];
  required: boolean; invalid: boolean; describedBy?: string;
  onChange: (value: number | string | null) => void;
}

const NONE = "__none__";
const PLACEHOLDER = "— select —";
/** Lists longer than this get a search box (countries, MedDRA-like lists). */
const SEARCH_FROM = 12;

function isSelected(option: CaseFieldOption, value: number | string | null): boolean {
  return String(option.id) === String(value);
}

/** Inactive codelist rows stay out of the list unless they are the stored value; blank labels never show. */
function offered(options: CaseFieldOption[], value: number | string | null): CaseFieldOption[] {
  return options.filter((option) => option.label.trim() !== "" && (option.active !== false || isSelected(option, value)));
}

/** Short codelists: a plain dropdown panel. */
function PlainSelect({ id, value, options, required, invalid, describedBy, onChange }: Props) {
  return <SelectPrimitive.Root value={value == null ? "" : String(value)} onValueChange={(next) =>
    onChange(next === NONE || next === "" ? null : options.find((option) => String(option.id) === next)?.id ?? null)}>
    <SelectPrimitive.Trigger id={id} className="cr-select-trigger" aria-required={required} aria-invalid={invalid} aria-describedby={describedBy} data-empty={value == null}>
      <SelectPrimitive.Value placeholder={PLACEHOLDER} />
      <SelectPrimitive.Icon className="cr-select-chevron"><CaseReviewIcon name="chevronDown" size={12} strokeWidth={2.5} /></SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content className="cr-portal cr-select-content" position="popper" sideOffset={4} align="start">
        <SelectPrimitive.Viewport className="cr-select-viewport">
          {value != null && <SelectPrimitive.Item value={NONE} className="cr-select-item cr-select-item-clear"><SelectPrimitive.ItemText>{EMPTY_VALUE_LABEL}</SelectPrimitive.ItemText></SelectPrimitive.Item>}
          {options.map((option) => <SelectPrimitive.Item key={option.id} value={String(option.id)} className="cr-select-item" data-inactive={option.active === false}>
            <span className="cr-select-check"><SelectPrimitive.ItemIndicator><CaseReviewIcon name="check" size={13} strokeWidth={2.6} /></SelectPrimitive.ItemIndicator></span>
            <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
          </SelectPrimitive.Item>)}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  </SelectPrimitive.Root>;
}

/** Long codelists: the same trigger, a searchable panel. */
function SearchSelect({ id, value, options, required, invalid, describedBy, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const label = options.find((option) => isSelected(option, value))?.label;
  return <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
    <PopoverPrimitive.Trigger id={id} className="cr-select-trigger" role="combobox" aria-expanded={open} aria-required={required} aria-invalid={invalid} aria-describedby={describedBy} data-empty={value == null}>
      <span className="cr-select-value">{label ?? PLACEHOLDER}</span>
      <span className="cr-select-chevron"><CaseReviewIcon name="chevronDown" size={12} strokeWidth={2.5} /></span>
    </PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content className="cr-portal cr-select-content cr-combobox" sideOffset={4} align="start">
        <Command className="cr-command" loop>
          <div className="cr-command-search"><CaseReviewIcon name="search" size={14} /><Command.Input placeholder="Search…" className="cr-command-input" autoFocus /></div>
          <Command.List className="cr-select-viewport">
            <Command.Empty className="cr-select-empty">No match.</Command.Empty>
            {value != null && <Command.Item value={EMPTY_VALUE_LABEL} className="cr-select-item cr-select-item-clear" onSelect={() => { onChange(null); setOpen(false); }}>{EMPTY_VALUE_LABEL}</Command.Item>}
            {options.map((option) => <Command.Item key={option.id} value={`${option.label} ${option.id}`} className="cr-select-item" data-inactive={option.active === false}
              onSelect={() => { onChange(option.id); setOpen(false); }}>
              <span className="cr-select-check">{isSelected(option, value) && <CaseReviewIcon name="check" size={13} strokeWidth={2.6} />}</span>
              <span className="cr-select-value">{option.label}</span>
            </Command.Item>)}
          </Command.List>
        </Command>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>;
}

/** Coded value picker. Values stay codelist ids; labels are display only. */
export function CaseFieldSelect(props: Props) {
  const options = offered(props.options, props.value);
  return options.length > SEARCH_FROM ? <SearchSelect {...props} options={options} /> : <PlainSelect {...props} options={options} />;
}
